import axios, { AxiosResponse } from 'axios';
import { getFromLocalStorage } from 'core/utils/localStorage';

const OAUTH_CLIENT_ID = process.env.REACT_APP_OAUTH_CLIENT_ID ?? '';
const OAUTH_SECRET = process.env.REACT_APP_OAUTH_SECRET ?? '';
const ACCOUNTS_ENDPOINT = process.env.REACT_APP_ACCOUNTS_ENDPOINT ?? '';
const BASE_API_URL = process.env.REACT_APP_BASE_API_URL ?? '';

const CLIENTIDAUTHCODE_ENC = btoa(`${OAUTH_CLIENT_ID}:${OAUTH_SECRET}`);

export const OAUTH_TOKEN_HEADERS = {
  'Content-Type': 'application/x-www-form-urlencoded',
  authorization: `Basic ${CLIENTIDAUTHCODE_ENC}`,
};

export const generateOAuthState = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const state = Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
  sessionStorage.setItem('oauth_state', state);
  return state;
};

export const validateOAuthState = (returnedState: string | null): boolean => {
  const storedState = sessionStorage.getItem('oauth_state');
  sessionStorage.removeItem('oauth_state');
  return !!storedState && storedState === returnedState;
};

export const buildSSOLoginURL = (redirectPath: string) => {
  const redirectUri = `${window.location.origin}${redirectPath}`;
  const state = generateOAuthState();
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: OAUTH_CLIENT_ID,
    redirect_uri: redirectUri,
    state,
  });
  return `https://${ACCOUNTS_ENDPOINT}/oauth/authorize?${params.toString()}`;
};

/**
 * Computed at call time (not module load) so it reflects wherever the user
 * currently is — e.g. `/au/app/...` — rather than whatever path happened to
 * be current the first time this module was imported. Includes the pathname
 * so the accounts SSO logout page's `continue` redirect lands back on the
 * user's country route instead of the bare origin (which the app then can't
 * tell apart from a fresh visit, and falls back to the default country).
 */
export const getSSOLogoutURL = (): string =>
  `https://${ACCOUNTS_ENDPOINT}/exit?continue=${window.location.origin}${window.location.pathname}`;

export interface IAuthTokenResponse {
  access_token: string;
  refresh_token: string;
}

export interface IUserProfile {
  email: string;
  id?: string;
  name?: string;
  [key: string]: any;
}

export const fetchTokens = async (code: string): Promise<IAuthTokenResponse> => {
  const redirectUri = `${window.location.origin}${window.location.pathname}`;
  const payload = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: OAUTH_CLIENT_ID,
    redirect_uri: redirectUri,
    code,
  });

  const response = await axios.post<any, AxiosResponse<IAuthTokenResponse>>(
    `${BASE_API_URL}/oauth/token`,
    payload.toString(),
    { headers: OAUTH_TOKEN_HEADERS }
  );

  const tokens = response.data;
  localStorage.setItem('tokens', JSON.stringify(tokens));
  return tokens;
};

export const fetchCurrentUserDetails = async (): Promise<IUserProfile> => {
  const tokens = getFromLocalStorage('tokens');
  const accessToken = tokens?.access_token ?? '';

  const headers = {
    'Content-Type': 'application/json',
    authorization: `Bearer ${accessToken}`,
  };

  const res = await axios.get<IUserProfile>(
    `https://${ACCOUNTS_ENDPOINT}/v2/accounts/me?exclude_attributes=authorities`,
    { headers }
  );

  return res.data;
};

export const getSSOAccessToken = (): string => {
  const tokens = getFromLocalStorage('tokens');
  return tokens?.access_token ?? '';
};

export const isSSOTokenAvailable = (): boolean => {
  const tokens = getFromLocalStorage('tokens');
  return Boolean(tokens?.access_token);
};

export const isLogoutInitiated = (): boolean =>
  localStorage.getItem('sso_logout_initiated') === 'true';

export const trackSSOLogoutOperation = (): void => {
  localStorage.setItem('sso_logout_initiated', 'true');
};

export const untrackSSOLogoutOperation = (): void => {
  localStorage.removeItem('sso_logout_initiated');
};

export const logoutSSOUserFromApp = (): void => {
  window.location.href = getSSOLogoutURL();
};

// ---------------------------------------------------------------------------
// Cropwise Portal login via /{countryCode}/authenticate callback
// Controlled by the isFarmSettingsEnabled feature flag (allowedCountries list).
// To enable for a new country, add it to allowedCountries in the flag condition —
// no code changes required.
// ---------------------------------------------------------------------------

/**
 * Builds the OAuth authorize URL for the "Login with Cropwise" button.
 * redirect_uri is always {origin}/{countryCode}/authenticate so it works
 * for any country enabled in the isFarmSettingsEnabled feature flag.
 */
export const buildCropwisePortalLoginURL = (): string => {
  const pathname = window.location.pathname.endsWith('/')
    ? window.location.pathname
    : `${window.location.pathname}/`;
  const redirectUri = `${window.location.origin}${pathname}authenticate`;
  return `https://${ACCOUNTS_ENDPOINT}/oauth/authorize?response_type=code&client_id=${OAUTH_CLIENT_ID}&redirect_uri=${redirectUri}`;
};

/**
 * Exchanges the authorization code for tokens on the /{countryCode}/authenticate route.
 * Uses window.location.pathname as redirect_uri (which IS /{countryCode}/authenticate
 * at callback time), so this works for any country without configuration.
 * Delegates to fetchTokens since the token exchange logic is identical.
 */
export const fetchTokensForAuthenticateRoute = fetchTokens;
